import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Target } from 'lucide-react';
import type { CampaignMetadata, MetricDefinition } from '@monitoring-panel/types';
import { Modal } from './Modal';
import {
  useAISuggestMetrics,
  type AIMetricSuggestion,
} from '@/hooks/useAISuggestMetrics';
import {
  useBulkAddCustomMetrics,
  type BulkCustomMetric,
} from '@/hooks/useMetricConfig';
import { cn } from '@/lib/utils';

interface AIMetricSuggestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  executionId: string;
  metadata: CampaignMetadata;
  availableColumns: string[];
  existingMetrics: MetricDefinition[];
  campaignData: Array<Record<string, unknown>>;
}

export function AIMetricSuggestionsModal({
  isOpen,
  onClose,
  executionId,
  metadata,
  availableColumns,
  existingMetrics,
  campaignData,
}: AIMetricSuggestionsModalProps) {
  const aiSuggest = useAISuggestMetrics();
  const bulkAdd = useBulkAddCustomMetrics(executionId);

  const [accepted, setAccepted] = useState<AIMetricSuggestion[]>([]);
  const [rejected, setRejected] = useState<
    Array<{ suggestion: AIMetricSuggestion; reason: string }>
  >([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [hasRun, setHasRun] = useState(false);

  // Trigger generation on open, reset on close
  useEffect(() => {
    if (!isOpen) {
      setAccepted([]);
      setRejected([]);
      setSelected(new Set());
      setHasRun(false);
      aiSuggest.reset();
      return;
    }
    if (hasRun) return;
    setHasRun(true);
    aiSuggest.mutate(
      {
        metadata,
        availableColumns,
        existingMetrics,
        campaignData,
      },
      {
        onSuccess: (result) => {
          setAccepted(result.accepted);
          setRejected(result.rejected);
          setSelected(new Set(result.accepted.map((_, idx) => idx)));
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const toggle = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleAdd = () => {
    const picked: BulkCustomMetric[] = accepted
      .filter((_, idx) => selected.has(idx))
      .map((s) => ({
        name: s.name,
        formula: s.formula,
        format: s.format,
        decimals: s.decimals,
        goal: s.goal
          ? {
              target: s.goal.target,
              direction: s.goal.direction,
              greenBoundary: s.goal.greenBoundary,
              amberBoundary: s.goal.amberBoundary,
            }
          : undefined,
      }));
    if (picked.length === 0) return;
    bulkAdd.mutate(picked, {
      onSuccess: () => onClose(),
    });
  };

  const isLoading = aiSuggest.isPending;
  const error = aiSuggest.error;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Suggest metrics with Vera" maxWidth="2xl">
      <div className="space-y-4">
        {isLoading && (
          <div className="flex items-center justify-center gap-3 py-12 text-adv-secondary">
            <Loader2 size={20} className="animate-spin text-adv-accent" />
            <span className="text-adv-body-m">Vera is analyzing the campaign…</span>
          </div>
        )}

        {error && (
          <div className="adv-alert flex items-start gap-2">
            <AlertCircle size={16} className="mt-1 flex-shrink-0" />
            <div>
              <p className="font-bold">Suggestion failed</p>
              <p className="text-adv-body-s">{error.message}</p>
            </div>
          </div>
        )}

        {!isLoading && !error && accepted.length === 0 && rejected.length === 0 && hasRun && (
          <div className="adv-state__desc text-center py-8">
            No suitable metrics found for this campaign's available columns.
          </div>
        )}

        {!isLoading && accepted.length > 0 && (
          <div className="space-y-2">
            <p className="adv-kpi__label">
              Suggested metrics ({accepted.length})
            </p>
            {accepted.map((s, idx) => {
              const isSelected = selected.has(idx);
              return (
                <label
                  key={idx}
                  className={cn(
                    'block border rounded-adv-lg p-3 cursor-pointer transition-colors',
                    isSelected
                      ? 'border-adv-gold bg-adv-gold-veil'
                      : 'border-adv-border hover:border-adv-border-strong'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(idx)}
                      className="adv-check mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-adv-primary">{s.name}</span>
                        <span className="adv-tag adv-tag--default">
                          {s.format}
                        </span>
                        {s.goal && (
                          <span className="adv-tag adv-tag--gold">
                            <Target size={10} />
                            target <span className="adv-num">{s.goal.target}</span>
                          </span>
                        )}
                      </div>
                      <code className="block mt-1 text-adv-code-xs font-adv-mono text-adv-primary bg-adv-card border border-adv-border rounded-adv-sm px-2 py-1">
                        {s.formula}
                      </code>
                      <p className="mt-1 text-adv-body-s text-adv-secondary">{s.rationale}</p>
                      {s.goal?.rationale && (
                        <p className="mt-1 text-adv-body-s text-adv-primary">
                          Goal: {s.goal.rationale}
                        </p>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {!isLoading && rejected.length > 0 && (
          <details className="border border-adv-border rounded-adv-lg">
            <summary className="px-3 py-2 text-adv-body-s text-adv-secondary cursor-pointer hover:bg-adv-sunken rounded-adv-lg">
              {rejected.length} suggestion{rejected.length === 1 ? '' : 's'} rejected by validation
            </summary>
            <div className="px-3 pb-3 space-y-2">
              {rejected.map((r, idx) => (
                <div key={idx} className="text-adv-body-s">
                  <p className="font-bold text-adv-primary">{r.suggestion.name}</p>
                  <code className="block font-adv-mono text-adv-secondary">{r.suggestion.formula}</code>
                  <p className="text-adv-warning-text">{r.reason}</p>
                </div>
              ))}
            </div>
          </details>
        )}

        {!isLoading && (accepted.length > 0 || rejected.length > 0) && (
          <div className="flex justify-end gap-2 pt-2 border-t border-adv-border">
            <button
              onClick={onClose}
              className="adv-btn adv-btn--ghost"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={selected.size === 0 || bulkAdd.isPending}
              className="adv-btn adv-btn--primary"
            >
              {bulkAdd.isPending
                ? 'Adding…'
                : `Add ${selected.size} metric${selected.size === 1 ? '' : 's'}`}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
